<template>
  <div
    v-if="modelValue"
    class="som-host-modal-root"
    role="dialog"
    aria-modal="true"
    aria-labelledby="somHostTitle"
  >
    <div class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 id="somHostTitle" class="modal-title">Select-o-Matic</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="close" />
          </div>
          <div class="modal-body">
            <div v-if="!gameId" class="alert alert-warning">Select a location with an active game.</div>

            <template v-else>
              <div v-if="!sessionActive" class="alert alert-secondary mb-3">
                Session is <strong>inactive</strong>. Players see the normal game screen until you start Select-o-Matic.
              </div>
              <div v-else class="small text-muted mb-3">
                <strong>Suit tiebreak</strong> for tied ranks (strongest first): {{ suitLegend }}.
                Call the two teams with the highest <em>chosen</em> cards to the stage.
              </div>

              <div v-if="ranked.length">
                <h6 class="text-success">Chosen card ranking</h6>
                <ol class="list-group list-group-numbered mb-3">
                  <li
                    v-for="(row, idx) in ranked"
                    :key="row.teamId"
                    class="list-group-item d-flex justify-content-between align-items-center"
                  >
                    <span>
                      <strong>#{{ idx + 1 }}</strong> {{ row.teamName }}
                      <span class="text-muted small">({{ row.teamId }})</span>
                      <span class="badge bg-secondary ms-1">Wins: {{ winCount(row.teamId) }}</span>
                    </span>
                    <span class="badge bg-primary rounded-pill">{{ row.cardLabel }}</span>
                  </li>
                </ol>
                <p v-if="ranked.length >= 2" class="alert alert-info py-2">
                  <strong>Top two:</strong> {{ ranked[0].teamName }} ({{ ranked[0].cardLabel }}) and
                  {{ ranked[1].teamName }} ({{ ranked[1].cardLabel }})
                </p>
              </div>

              <div v-if="waiting.length">
                <h6 class="text-warning">Waiting on card choice</h6>
                <ul class="list-group mb-3">
                  <li
                    v-for="row in waiting"
                    :key="row.teamId"
                    class="list-group-item d-flex justify-content-between flex-wrap gap-2"
                  >
                    <span>
                      {{ row.teamName }}
                      <span class="text-muted small">({{ row.teamId }})</span>
                      <span class="badge bg-secondary ms-1">Wins: {{ winCount(row.teamId) }}</span>
                    </span>
                    <span class="small text-muted">Dealt: {{ row.cardsLabel }}</span>
                  </li>
                </ul>
              </div>

              <div v-if="declined.length">
                <h6 class="text-secondary">Opted out</h6>
                <ul class="list-group mb-3">
                  <li v-for="row in declined" :key="row.teamId" class="list-group-item">
                    {{ row.teamName }}
                    <span class="text-muted small">({{ row.teamId }})</span>
                    <span class="badge bg-secondary ms-1">Wins: {{ winCount(row.teamId) }}</span>
                  </li>
                </ul>
              </div>

              <p v-if="sessionActive && !ranked.length && !waiting.length && !declined.length" class="text-muted">
                No responses yet for this session.
              </p>
            </template>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-danger"
              :disabled="!gameId || busy || !sessionActive"
              @click="onEnd"
            >
              End Select-o-Matic
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" />
  </div>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue';
import { onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import {
  sessionDocRef,
  finalizeSelectOMaticSession,
  selectOMaticParticipantsCollection,
  selectOMaticWinsCollection,
} from '@/services/selectOMaticService';
import { cardIdToLabel, compareCardIds, SUIT_RANKING_DISPLAY } from '@/utils/playingCards';

export default {
  name: 'SelectOMaticHostModal',
  props: {
    modelValue: { type: Boolean, default: false },
    gameId: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const sessionActive = ref(false);
    const liveSessionId = ref('');
    const participants = ref([]);
    const winsByTeam = ref({});
    const busy = ref(false);
    let unsubSession = null;
    let unsubParts = null;
    let unsubWins = null;

    const suitLegend = SUIT_RANKING_DISPLAY.join(', ');

    function winCount(teamId) {
      if (teamId == null) return 0;
      return winsByTeam.value[String(teamId)] || 0;
    }

    function close() {
      emit('update:modelValue', false);
    }

    const filtered = computed(() =>
      participants.value.filter((p) => p.sessionId === liveSessionId.value && liveSessionId.value)
    );

    const ranked = computed(() => {
      const rows = filtered.value
        .filter((p) => !p.declined && p.chosenCard != null)
        .map((p) => ({
          teamId: p.teamId,
          teamName: p.teamName || 'Team',
          cardLabel: cardIdToLabel(p.chosenCard),
          chosenCard: p.chosenCard,
        }));
      rows.sort((a, b) => compareCardIds(b.chosenCard, a.chosenCard));
      return rows;
    });

    const waiting = computed(() =>
      filtered.value
        .filter((p) => !p.declined && Array.isArray(p.cards) && p.cards.length === 2 && p.chosenCard == null)
        .map((p) => ({
          teamId: p.teamId,
          teamName: p.teamName || 'Team',
          cardsLabel: p.cards.map((c) => cardIdToLabel(c)).join(' · '),
        }))
    );

    const declined = computed(() =>
      filtered.value.filter((p) => p.declined).map((p) => ({
        teamId: p.teamId,
        teamName: p.teamName || 'Team',
      }))
    );

    function detach() {
      if (unsubSession) {
        unsubSession();
        unsubSession = null;
      }
      if (unsubParts) {
        unsubParts();
        unsubParts = null;
      }
      if (unsubWins) {
        unsubWins();
        unsubWins = null;
      }
    }

    function attach() {
      detach();
      if (!props.gameId || !props.modelValue) return;

      const sRef = sessionDocRef(db, props.gameId);
      unsubSession = onSnapshot(sRef, (snap) => {
        if (!snap.exists()) {
          sessionActive.value = false;
          liveSessionId.value = '';
          return;
        }
        const d = snap.data();
        sessionActive.value = !!d.active;
        liveSessionId.value = d.sessionId || '';
      });

      const partsRef = selectOMaticParticipantsCollection(db, props.gameId);
      unsubParts = onSnapshot(partsRef, (snap) => {
        participants.value = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
      });

      const winsRef = selectOMaticWinsCollection(db, props.gameId);
      unsubWins = onSnapshot(winsRef, (snap) => {
        const m = {};
        snap.docs.forEach((d) => {
          m[d.id] = Number(d.data().wins || 0);
        });
        winsByTeam.value = m;
      });
    }

    watch(
      () => [props.gameId, props.modelValue],
      () => {
        attach();
      },
      { immediate: true }
    );

    onUnmounted(() => detach());

    async function onEnd() {
      if (!props.gameId || !sessionActive.value) return;
      if (!window.confirm('End Select-o-Matic for everyone at this game?')) return;
      busy.value = true;
      try {
        await finalizeSelectOMaticSession(db, props.gameId);
      } catch (e) {
        console.error(e);
        alert(e.message || 'Could not end session.');
      } finally {
        busy.value = false;
      }
    }

    return {
      close,
      sessionActive,
      ranked,
      waiting,
      declined,
      suitLegend,
      busy,
      onEnd,
      winCount,
    };
  },
};
</script>

<style scoped>
.som-host-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1060;
}
.modal {
  z-index: 1065;
}
</style>
